/**
 * Key helpers — a leaf, importing nothing: the client imports this module directly
 * (`domain/routes.ts`, `rowLabel.ts`, `entityTypes.ts`), so client and server can never split
 * a key differently.
 */

/**
 * The one child-key separator: `<parentKey>/<rest>` everywhere a key is composed — a game's
 * key (`gameKey`/`splitGameKey` below), the master partition's child prefix (`data/keys.ts`) and
 * the id schemas that refuse it in a leaf id (`router/schemas.ts`). Three independent copies of
 * `'/'` is the bug this constant exists to prevent: they must not be able to disagree.
 */
export const KEY_SEPARATOR = '/';

/**
 * A game's key is `<studio>/<id>`. Game ids are globally unique slugs, so the composite is
 * unique too; composing the studio in is what makes the studio the game's parent for the
 * tree, the plan order and the prefix queries — the same `<parentKey>/<rest>` rule every
 * child type in the reference hub follows. A game moved to another studio in one
 * environment therefore reads as `missing` under the old key and `env_only` under the new
 * one, which is the honest reading.
 */
export const gameKey = (studio: string, id: string): string => `${studio}${KEY_SEPARATOR}${id}`;

/**
 * Split on the FIRST separator — a studio id never contains one, a game id might. Null when there
 * is no separator or the studio part is empty; total, so it is safe during render.
 */
export const parseGameKey = (key: string): { studio: string; id: string } | null => {
    const index = key.indexOf(KEY_SEPARATOR);
    if (index <= 0) return null;
    return { studio: key.slice(0, index), id: key.slice(index + 1) };
};

/** `parseGameKey` for a key that must be well formed: throws on one that is not, or has no id. */
export const splitGameKey = (key: string): { studio: string; id: string } => {
    const parsed = parseGameKey(key);
    if (!parsed || parsed.id === '') throw new Error(`game key "${key}" is not <studio>/<id>`);
    return parsed;
};

/** `${type}/${key}` — the de-dupe key for a set of type/key refs. */
export const refKey = (ref: { type: string; key: string }): string => `${ref.type}/${ref.key}`;
